import gql from 'graphql-tag'
import type { DocumentNode } from '@apollo/client/core'
import { getApolloClient } from '@/lib/apollo-client'
import { locationToSlippyTileName } from '@/lib/math'
import type {
  OffroadRouteQuickSearchResponse,
  QuickSearchBaseResponse,
  QuickSearchFilter,
  QuickSearchResult,
} from '@/lib/quick-search/types'
import { bboxFromVisibleRegion, type BBox } from '@/lib/geographic'
// eslint-disable-next-line no-duplicate-imports
import type Geographic from '@/lib/geographic'
import { camera, visibleRegion } from '@/services/map-service'
import { milesToMeters } from '@/components/shared/x-distance.vue'
import type { KgType } from '@/lib/knowledge-graph/types'

/** Baseline fragment to use in all Quick Search queries */
const quickSearchResultFragment = gql`
  fragment quick_search_result_fragment on Place {
    __typename
    id
    name
    extent
    location
  }
`

/** GQL query for nearby HikeRoutes to be shown in Quick Search */
const hikeRoutesQuickSearchQuery = gql`
  query hikeRoutesQuickSearchQuery($filter: HikeRouteFilter, $limit: Int!) {
    hikeRoutes(filter: $filter, limit: $limit) {
      ...quick_search_result_fragment
      photos(limit: 1) {
        contentUrl(width: 300, resizeMode: FILL)
      }
      near {
        name
        state {
          abbreviation
        }
      }
    }
  }
  ${quickSearchResultFragment}
`

/** Async data request for HikeRoutes, mapping to common QuickSearchResult output */
export const requestNearbyHikesInCategory = async (
  filters: QuickSearchFilter,
  enforceBbox = false
): Promise<QuickSearchResult[]> => {
  const response = await fetchNearbyEntities<{
    hikeRoutes: QuickSearchBaseResponse[]
  }>(hikeRoutesQuickSearchQuery, filters, enforceBbox)
  const hikeResults = response?.data.hikeRoutes ?? []

  return hikeResults.map((result) => {
    const { __typename, id, name, location, extent, near, photos } = result
    const imageUrl = photos?.[0]?.contentUrl
    const nearestThing = near?.[0]
    const nearString = nearestThing
      ? `Near ${nearestThing.name}, ${nearestThing.state.abbreviation}`
      : ''

    return {
      __typename: __typename as KgType,
      id,
      name,
      location,
      extent,
      near: nearString,
      imageUrl,
    }
  })
}

/** GQL query for nearby OffroadRoutes to be shown in Quick Search */
const offroadRoutesQuickSearchQuery = gql`
  query offroadRoutesQuickSearchQuery(
    $filter: OffroadRouteFilter
    $limit: Int!
  ) {
    offroadRoutes(filter: $filter, limit: $limit) {
      ...quick_search_result_fragment
      photos(limit: 1) {
        contentUrl(width: 300, resizeMode: FILL)
      }
      near {
        name
        state {
          abbreviation
        }
      }
      richPlace {
        id
      }
    }
  }
  ${quickSearchResultFragment}
`

/** Async data request for HikeRoutes, mapping to common QuickSearchResult output */
export const requestNearbyOffroadRoutesInCategory = async (
  filters: QuickSearchFilter,
  enforceBbox = false
): Promise<QuickSearchResult[]> => {
  const response = await fetchNearbyEntities<{
    offroadRoutes: OffroadRouteQuickSearchResponse[]
  }>(offroadRoutesQuickSearchQuery, filters, enforceBbox)
  const offroadResults = response?.data.offroadRoutes ?? []

  return offroadResults.map((result) => {
    const { __typename, id, name, location, extent, near, photos } = result
    const imageUrl = photos?.[0]?.contentUrl
    const nearestThing = near?.[0]
    const nearString = nearestThing
      ? `Near ${nearestThing.name}, ${nearestThing.state.abbreviation}`
      : ''

    return {
      __typename: __typename as KgType,
      id,
      name,
      location,
      extent,
      near: nearString,
      imageUrl,
      richPlaceId: result.richPlace?.id,
    }
  })
}

/// QUICK SEARCH QUERY UTILITIES ///
/**
 * Generic data fetching function for nearby Knowledge Graph entities for quick search.
 * The specific Knowledge Graph query and filters to use in the request are passed as parameters.
 * By default this function filters the query by the current camera location, but it can be used to enforce the current
 * map bounding box and only search within those bounds.
 */
export const fetchNearbyEntities = async <T>(
  query: DocumentNode,
  filters: QuickSearchFilter,
  enforceBbox = false
): Promise<{ data: T }> => {
  const client = getApolloClient()

  // Set filters for request
  let requestFilter: QuickSearchFilter

  if (enforceBbox) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const bbox = bboxFromVisibleRegion(visibleRegion())!
    requestFilter = { ...getByBboxFilter(bbox, filters) }
  } else {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const currentCamera = camera()!
    requestFilter = { ...getByLocationFilter(currentCamera.location, filters) }
  }

  return await client.query<T>({
    query,
    variables: { limit: 25, filter: requestFilter },
    fetchPolicy: 'network-only',
  })
}

const DEFAULT_NEAR_LOCATION_DISTANCE = milesToMeters(100)

/**
 * Returns a filter for quick search queries that searches for entities near a given location.
 */
export const getByLocationFilter = (
  location: Geographic,
  filter: QuickSearchFilter = {},
  distance: number = DEFAULT_NEAR_LOCATION_DISTANCE
): QuickSearchFilter => {
  const { latitude, longitude } = location

  // Static z level of 13 for high precision in Quick Search queries
  const { x, y, z } = locationToSlippyTileName(latitude, longitude, 13)

  return {
    ...filter,
    location: {
      nearTile: {
        z,
        x,
        y,
        distance,
      },
    },
  }
}

/**
 * Returns a filter for quick search queries that searches for entities within a given bounding box.
 */
export const getByBboxFilter = (
  bbox: BBox,
  filter: QuickSearchFilter = {}
): QuickSearchFilter => {
  const { bottom, left, right, top } = bbox
  return {
    ...filter,
    location: {
      withinBounds: {
        bottom,
        left,
        right,
        top,
      },
    },
  }
}
